import Phaser from "phaser";
import { addMuteButton } from "../game/MuteButton";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText } from "../game/view/layout";

const NARROW_WIDTH = 600;
const EDGE = 16; // min gap between text and the canvas edge
const NARROW_INDENT = 20; // design units (x0.5 on mobile) for wrapped descriptions
const PAIR_LINE_GAP = 3; // label -> description gap when stacked
const TABLE_COL_GAP = 8; // min gap between auto-fit table columns
const MIN_FONT_PX = 8;
const WRAP_LINE_SPACING = 5; // extra px between wrapped caption lines
const BACK_BOTTOM = 18; // BACK TO MENU center above the canvas bottom
const NAV_ROW_STEP = 26; // pager row center above BACK TO MENU center

class DocsScene extends Phaser.Scene {
  constructor() {
    super({ key: "DocsScene" });
  }

  create() {
    this.cameras.main.setBackgroundColor("#0a0a0a");

    const W = this.scale.width;
    const H = this.scale.height;
    const CX = W / 2;
    this._W = W;
    this._H = H;
    this._CX = CX;
    this._S = W / 800; // Scale factor for page content positions
    this._narrow = W < NARROW_WIDTH;

    this.page = 0;
    this.pages = [
      this.pageHowToPlay,
      this.pageWorlds,
      this.pageEnergy,
      this.pageRobots,
      this.pageCoins,
      this.pageTokenomics,
      this.pageEpochs,
      this.pageFairPlay,
    ];
    this.pageContainer = this.add.container(0, 0);

    // ── Navigation (created BEFORE showPage so updateNav works) ──
    // Pager row and BACK TO MENU sit on separate rows with a clear gap.
    const backY = H - BACK_BOTTOM;
    const navY = backY - NAV_ROW_STEP;
    this.prevBtn = this.add
      .text(W * 0.125, navY, "< PREV", fontStyle(FONT_SIZE.BODY, "#999999"))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.prevBtn.on("pointerdown", () => this.showPage(this.page - 1));

    this.pageLabel = this.add
      .text(CX, navY, "", fontStyle(FONT_SIZE.CAPTION, "#999999"))
      .setOrigin(0.5);

    this.nextBtn = this.add
      .text(W * 0.875, navY, "NEXT >", fontStyle(FONT_SIZE.BODY, "#00FFFF"))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.nextBtn.on("pointerdown", () => this.showPage(this.page + 1));

    // Back
    const back = this.add
      .text(CX, backY, "BACK  TO  MENU", fontStyle(FONT_SIZE.BODY, "#D4884A"))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    back.on("pointerdown", () => this.scene.start("MenuScene"));
    this.input.keyboard.on("keydown-ESC", () => this.scene.start("MenuScene"));
    this.input.keyboard.on("keydown-RIGHT", () => this.showPage(this.page + 1));
    this.input.keyboard.on("keydown-LEFT", () => this.showPage(this.page - 1));

    addMuteButton(this);

    // Show first page AFTER nav elements exist
    this.showPage(0);
  }

  showPage(idx) {
    if (idx < 0 || idx >= this.pages.length) return;
    this.page = idx;
    this.pageContainer.removeAll(true);
    this.pages[idx].call(this);
    this.updateNav();
  }

  updateNav() {
    this.pageLabel.setText(`${this.page + 1} / ${this.pages.length}`);
    this.prevBtn.setFill(this.page > 0 ? "#00FFFF" : "#777777");
    this.nextBtn.setFill(
      this.page < this.pages.length - 1 ? "#00FFFF" : "#777777",
    );
  }

  /** Left-aligned text at design x (800-wide units), shrunk to fit the canvas. */
  t(x, y, text, size, color) {
    const px = Math.round(x * this._S);
    const obj = this.add.text(px, y, text, fontStyle(size, color));
    fitText(obj, this._W - EDGE - px);
    this.pageContainer.add(obj);
    return obj;
  }

  /** Centered text, shrunk to fit the canvas width minus margins. */
  c(y, text, size, color) {
    const obj = this.add
      .text(this._CX, y, text, fontStyle(size, color))
      .setOrigin(0.5);
    fitText(obj, this._W - EDGE * 2);
    this.pageContainer.add(obj);
    return obj;
  }

  /** Caption that word-wraps at the canvas edge instead of shrinking. */
  wrapped(x, y, text, color) {
    const px = Math.round(x * this._S);
    const obj = this.add.text(px, y, text, {
      ...fontStyle(FONT_SIZE.CAPTION, color),
      wordWrap: { width: this._W - EDGE - px },
      lineSpacing: WRAP_LINE_SPACING,
    });
    this.pageContainer.add(obj);
    return obj;
  }

  /**
   * Label + description row. Desktop: description in a column at descX.
   * Narrow: description on its own indented line under the label.
   * Returns the y for the next row.
   */
  pair(x, y, label, descX, desc, step) {
    const [labelText, labelSize, labelColor] = label;
    const [descText, descColor] = desc;
    const labelObj = this.t(x, y, labelText, labelSize, labelColor);
    if (!this._narrow) {
      this.t(descX, y, descText, FONT_SIZE.CAPTION, descColor);
      return y + step;
    }
    const descY = y + labelObj.height + PAIR_LINE_GAP;
    const descObj = this.wrapped(x + NARROW_INDENT, descY, descText, descColor);
    return descY + Math.max(step, descObj.height + PAIR_LINE_GAP);
  }

  /**
   * Table with a header row, divider and data rows (cells are [text, color]).
   * Desktop keeps the hand-placed columns; narrow screens auto-fit the
   * columns to the canvas width. Returns the y below the last row.
   */
  table(y, { headers, headerXs, rowXs, rows, rowStep, lineColor, lineAlpha }) {
    const grid = [];
    grid.push(
      headers.map((h, i) =>
        this.t(headerXs[i], y, h, FONT_SIZE.CAPTION, "#999999"),
      ),
    );
    let cursor = y + 14;
    this.line(cursor, lineColor, lineAlpha);
    cursor += 6;
    rows.forEach((cells) => {
      grid.push(
        cells.map(([text, color], i) =>
          this.t(rowXs[i], cursor, text, FONT_SIZE.CAPTION, color),
        ),
      );
      cursor += rowStep;
    });
    if (this._narrow) this.fitColumns(grid);
    return cursor;
  }

  /** Shrink a grid of texts together and spread its columns across the width. */
  fitColumns(grid) {
    const cols = grid[0].length;
    const avail = this._W - EDGE * 2;
    const colWidths = () =>
      grid[0].map((_, c) => Math.max(...grid.map((row) => row[c].width)));
    const total = () =>
      colWidths().reduce((a, b) => a + b, 0) + TABLE_COL_GAP * (cols - 1);

    let size = FONT_SIZE.CAPTION;
    while (total() > avail && size > MIN_FONT_PX) {
      size -= 1;
      for (const obj of grid.flat()) obj.setFontSize(size);
    }

    const widths = colWidths();
    const used = widths.reduce((a, b) => a + b, 0);
    const gap =
      cols > 1 ? Math.max(TABLE_COL_GAP, (avail - used) / (cols - 1)) : 0;
    let x = EDGE;
    widths.forEach((w, c) => {
      grid.forEach((row) => row[c].setX(Math.round(x)));
      x += w + gap;
    });
  }

  line(y, color, alpha) {
    const gfx = this.add.graphics();
    this.pageContainer.add(gfx);
    gfx.lineStyle(1, color, alpha || 0.15);
    gfx.lineBetween(Math.round(60 * this._S), y, Math.round(740 * this._S), y);
    return gfx;
  }

  /** Rows of [label, color, description] as label/description pairs. */
  pairs(x, y, descX, rows, step) {
    rows.forEach(([label, color, desc]) => {
      y = this.pair(
        x,
        y,
        [label, FONT_SIZE.CAPTION, color],
        descX,
        [desc, "#888888"],
        step,
      );
    });
    return y;
  }

  /** Caption lines, one per row; a long line wraps and pushes the rest down. */
  lines(x, y, rows, step) {
    rows.forEach(([text, color]) => {
      const obj = this.wrapped(x, y, text, color);
      y += Math.max(step, obj.height + PAIR_LINE_GAP);
    });
    return y;
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 1: HOW TO PLAY
  // ═══════════════════════════════════════════════════════════
  pageHowToPlay() {
    this.c(18, "DAMS TO AGI", FONT_SIZE.HEADING, "#FFD700");

    const x = 70;
    let y = 50;

    this.t(x, y, "CHUCK WOOD TO BUILD THE WAY UP.", FONT_SIZE.BODY, "#7BC74A");
    y += 18;
    this.t(
      x,
      y,
      "JUMP TO SURVIVE. KEEP THE LIGHTS ON.",
      FONT_SIZE.BODY,
      "#00FFFF",
    );
    y += 26;

    this.t(x, y, "CONTROLS:", FONT_SIZE.CAPTION, "#888888");
    y += 18;
    y = this.pairs(
      x,
      y,
      x + 190,
      [
        ["MOVE", "#FFFFFF", "Left / Right or A / D"],
        ["CLIMB", "#FFFFFF", "Up / Down on a ladder"],
        ["JUMP", "#FFFFFF", "SPACE - jump barrels and sparks"],
        ["CHUCK WOOD", "#FFD700", "Alternate W and C at a work spot"],
        ["MOBILE", "#FFFFFF", "D-pad, JUMP and the W | C pair"],
      ],
      18,
    );

    y += 8;
    this.line(y, 0x7bc74a, 0.2);
    y += 12;

    this.t(x, y, "BROKEN LADDERS:", FONT_SIZE.BODY, "#D4884A");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["Stand at the gap and chuck wood: W, C, W, C...", "#888888"],
        ["Every 2 good taps add a rung. Same key twice = no rung.", "#888888"],
        ["Chucking pins you in place - watch the barrels!", "#FF6666"],
      ],
      16,
    );

    y += 10;
    this.t(x, y, "BEAVER BITE:", FONT_SIZE.BODY, "#FF00FF");
    y += 20;
    this.lines(
      x + 10,
      y,
      [
        ["Grab it to smash barrels for a few seconds.", "#888888"],
        ["While it lasts you can't jump or climb.", "#888888"],
      ],
      16,
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 2: WORLDS & STAGES
  // ═══════════════════════════════════════════════════════════
  pageWorlds() {
    this.c(18, "TWO WORLDS", FONT_SIZE.HEADING, "#00FFFF");

    const x = 70;
    let y = 50;

    this.t(x, y, "ODD STAGES: SOLARPUNK BUILD", FONT_SIZE.BODY, "#7BC74A");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["Climb to the dam at the top. Kong-Bot rolls barrels.", "#888888"],
        ["Energy reserve starts at 8,000 Wh and drains 100 Wh/s.", "#888888"],
        ["The flood rises as it drains. Empty = flooded out.", "#888888"],
        ["Reach the top: what's left becomes your WOOD.", "#FFD700"],
        ["A hit or a flood costs a life. Lives carry on.", "#FF6666"],
      ],
      16,
    );

    y += 12;
    this.t(x, y, "EVEN STAGES: NEON DEFEND", FONT_SIZE.BODY, "#FF00FF");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["Survive 60 seconds. Your energy carries over.", "#888888"],
        ["Leeches drain turbines - chuck wood at them to pry off.", "#888888"],
        ["Saboteurs crack ladders - chuck wood to repair.", "#888888"],
        ["A spark hunts you. Run at it and jump over.", "#888888"],
        ["Your WOOD falls live until you stop them.", "#FF6666"],
      ],
      16,
    );

    y += 12;
    this.line(y, 0x00ffff, 0.2);
    y += 12;

    this.t(x, y, "THE ROAD TO AGI:", FONT_SIZE.BODY, "#FFD700");
    y += 20;
    this.lines(
      x + 10,
      y,
      [
        ["Each loop is faster: more barrels, quicker sparks.", "#888888"],
        ["Hydro, then solar and wind, grid, fusion...", "#888888"],
        ["Stage 100 is AGI. Your energy decides what it becomes.", "#00FFFF"],
      ],
      16,
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 3: ENERGY -> WOOD
  // ═══════════════════════════════════════════════════════════
  pageEnergy() {
    this.c(18, "ENERGY = WOOD", FONT_SIZE.HEADING, "#FFD700");

    const x = 70;
    let y = 50;

    this.t(x, y, "YOUR SCORE IS ENERGY (Wh).", FONT_SIZE.BODY, "#00FFFF");
    y += 18;
    this.t(x, y, "10 Wh = 1 LOG OF WOOD.", FONT_SIZE.BODY, "#FFD700");
    y += 26;

    y = this.table(y, {
      headers: ["ACTION", "ENERGY"],
      headerXs: [x, 520],
      rowXs: [x, 520],
      rows: [
        ["Build a broken ladder", "+500 Wh", "#39FF14"],
        ["Jump a barrel", "+100 Wh", "#39FF14"],
        ["Beaver Bite smash", "+300/500/800", "#39FF14"],
        ["Pry a leech off a turbine", "+200 Wh", "#39FF14"],
        ["Each leech feeding", "-20 Wh/s", "#FF6666"],
        ["Each cracked ladder", "-10 Wh/s", "#FF6666"],
        ["Hit (defend stage)", "-200 Wh", "#FF6666"],
        ["Play any stage (bonus)", "+500 Wh", "#7BC74A"],
        ["Clear stage N (bonus)", "+1,000 x N", "#7BC74A"],
      ].map(([action, wh, color]) => [
        [action, "#CCCCCC"],
        [wh, color],
      ]),
      rowStep: 17,
      lineColor: 0xffd700,
      lineAlpha: 0.2,
    });

    y += 14;
    this.line(y, 0xffd700, 0.2);
    y += 14;
    y = this.lines(
      x,
      y,
      [
        ["WOOD RISES WHEN YOU BUILD, FALLS WHEN AI DRAINS YOU.", "#FFFFFF"],
        ["Energy carries from stage to stage.", "#888888"],
      ],
      18,
    );
    y += 10;
    this.c(
      y,
      "BEAT THE CLASSIC ANSWER: 700 LB OF WOOD",
      FONT_SIZE.BODY,
      "#D4884A",
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 4: ROBOTS
  // ═══════════════════════════════════════════════════════════
  pageRobots() {
    this.c(18, "BAD ROBOTS", FONT_SIZE.HEADING, "#FF0000");

    const x = 70;
    let y = 50;

    this.t(
      x,
      y,
      "EACH ONE FOLLOWS A RULE YOU CAN LEARN:",
      FONT_SIZE.CAPTION,
      "#888888",
    );
    y += 22;

    y = this.pairs(
      x,
      y,
      x + 170,
      [
        ["KONG-BOT", "#FF6600", "Sits at the top and rolls barrels"],
        ["ROLLER", "#D4884A", "Rolls the girders, sometimes takes a ladder"],
        ["SPARK", "#00FFFF", "Charges past you, turns, comes back"],
        ["LEECH", "#FF00FF", "Latches on a turbine and drains energy"],
        ["SABOTEUR", "#FF6666", "Cracks ladders - they leak energy"],
      ],
      20,
    );

    y += 10;
    this.line(y, 0x39ff14, 0.2);
    y += 14;

    this.c(y, "GOOD ROBOTS ARE COMING", FONT_SIZE.BODY, "#39FF14");
    y += 22;
    this.lines(
      x,
      y,
      [
        ["Beaver-bots that help you build, repair drones,", "#888888"],
        ["and linemen who carry pried energy back to the grid.", "#888888"],
        [
          "The more energy the good side gets, the kinder AGI turns out.",
          "#FFD700",
        ],
      ],
      16,
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 5: INSERT COIN (ARCADE CREDITS)
  // ═══════════════════════════════════════════════════════════
  pageCoins() {
    this.c(18, "INSERT COIN", FONT_SIZE.HEADING, "#FFD700");

    const x = 70;
    let y = 50;

    this.t(x, y, "A FREE GAME EVERY DAY.", FONT_SIZE.BODY, "#39FF14");
    y += 18;
    this.t(x, y, "THEN $1 = 1 CREDIT = 3 LIVES.", FONT_SIZE.BODY, "#FFD700");
    y += 24;
    y = this.lines(
      x + 10,
      y,
      [
        ["Your lives carry from stage to stage.", "#888888"],
        ["Build stages: a hit or a flood costs a life.", "#888888"],
        ["Defend stages: being drained to zero costs a life.", "#888888"],
        ["Quitting in the middle of a stage costs a life too.", "#888888"],
      ],
      16,
    );

    y += 10;
    this.t(x, y, "OUT OF LIVES?  CONTINUE?", FONT_SIZE.BODY, "#FF6666");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["Insert a coin before the 10-second countdown ends:", "#888888"],
        ["you keep your stage and all your WOOD.", "#FFFFFF"],
        ["Let it run out and it's GAME OVER - back to stage 1.", "#888888"],
      ],
      16,
    );

    y += 10;
    this.line(y, 0x7bc74a, 0.2);
    y += 12;
    this.t(x, y, "WOOD JUST FOR PLAYING:", FONT_SIZE.BODY, "#7BC74A");
    y += 20;
    this.lines(
      x + 10,
      y,
      [
        ["+50 logs for every stage you play", "#FFFFFF"],
        ["+100 logs x the stage number for every clear", "#FFFFFF"],
        ["Your best WOOD balance is your epoch rank.", "#FFD700"],
      ],
      16,
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 7: $WOOD & EPOCHS
  // ═══════════════════════════════════════════════════════════
  pageEpochs() {
    this.c(18, "$WOOD PRIZE POT", FONT_SIZE.HEADING, "#9945FF");

    const x = 60;
    let y = 50;

    y = this.lines(
      x,
      y,
      [
        ["A FREE GAME EVERY DAY, THEN $1 PER CREDIT", "#FFFFFF"],
        ["Pay in SOL or USDT on Solana ($WOOD once it launches).", "#888888"],
        ["WOOD in the game is a score - it is not minted per play.", "#888888"],
      ],
      16,
    );

    y += 10;
    this.t(x, y, "30-DAY EPOCHS:", FONT_SIZE.BODY, "#00FFFF");
    y += 18;
    y = this.lines(
      x + 10,
      y,
      [
        ["Coins fill the pot.", "#888888"],
        ["You rank by your best verified WOOD balance.", "#888888"],
        ["The winner is paid from the pot in $WOOD (at launch).", "#FFD700"],
      ],
      16,
    );

    y += 8;
    this.line(y, 0xff00ff, 0.2);
    y += 10;
    this.t(x, y, "BEING FIRST MATTERS:", FONT_SIZE.CAPTION, "#888888");
    y += 16;

    y = this.pairs(
      x + 20,
      y,
      x + 200,
      [
        ["WINNER #1", "#FFD700", "100% OF PRIZE POT"],
        ["WINNER #2", "#C0C0C0", "90% OF NEW POT"],
        ["WINNER #3", "#CD7F32", "80% OF NEW POT"],
        ["#4 / #5", "#888888", "70% / 60%"],
        ["#6+", "#999999", "50% (FLOOR)"],
      ],
      15,
    );

    y += 4;
    this.t(
      x,
      y,
      "LEFTOVER ROLLS INTO THE NEXT POT",
      FONT_SIZE.CAPTION,
      "#00FFFF",
    );
    y += 20;
    this.t(x, y, "VESTING:", FONT_SIZE.BODY, "#9945FF");
    y += 18;
    this.t(
      x + 10,
      y,
      "40% NOW  -  30% AT 7 DAYS  -  30% AT 30 DAYS",
      FONT_SIZE.CAPTION,
      "#39FF14",
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 8: FAIR PLAY
  // ═══════════════════════════════════════════════════════════
  pageFairPlay() {
    this.c(18, "FAIR PLAY", FONT_SIZE.HEADING, "#FF0000");

    const x = 70;
    let y = 50;

    this.t(
      x,
      y,
      "EVERY STAGE IS REPLAYED ON THE SERVER",
      FONT_SIZE.BODY,
      "#39FF14",
    );
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["The server picks the stage seed.", "#888888"],
        ["Your game sends only your key presses.", "#888888"],
        [
          "The server replays them - its WOOD is the one that counts.",
          "#888888",
        ],
        ["A log faster than real time is rejected.", "#888888"],
      ],
      16,
    );

    y += 10;
    this.t(x, y, "STAGES 1-79: HUMANS ONLY", FONT_SIZE.BODY, "#00FFFF");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["Your W / C rhythm must look human.", "#888888"],
        ["Metronome-perfect chucking = RUN NOT VERIFIED.", "#FF6666"],
      ],
      16,
    );

    y += 10;
    this.line(y, 0xff6600, 0.3);
    y += 14;

    this.t(x, y, "STAGES 80-101: BOTS WELCOME", FONT_SIZE.BODY, "#FF6600");
    y += 20;
    y = this.lines(
      x + 10,
      y,
      [
        ["The rhythm check is off. Write a bot.", "#888888"],
        ["Humans vs bots - can your code beat the woodchuck?", "#FFD700"],
      ],
      16,
    );

    y += 10;
    this.wrapped(
      x,
      y,
      "FREE PLAY, THE DAILY STAGE AND CHALLENGES DON'T COUNT.",
      "#999999",
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PAGE 6: TOKENOMICS — STAGE-BASED SPLITS
  // ═══════════════════════════════════════════════════════════
  pageTokenomics() {
    this.c(18, "$WOOD TOKENOMICS", FONT_SIZE.HEADING, "#9945FF");

    const x = 60;
    let y = 48;

    this.t(x, y, "EVERY $1 PAYMENT IS SPLIT:", FONT_SIZE.CAPTION, "#888888");
    y += 16;
    this.t(x + 10, y, "20% OPS WALLET (FIXED)", FONT_SIZE.CAPTION, "#D4884A");
    y += 14;
    this.t(
      x + 10,
      y,
      "80% GAME ECONOMY (STAGE-BASED)",
      FONT_SIZE.CAPTION,
      "#00FFFF",
    );
    y += 20;

    this.t(x, y, "GAME ECONOMY SPLIT BY STAGE:", FONT_SIZE.CAPTION, "#888888");
    y += 16;

    // Table

    const tiers = [
      ["1-10", "50%", "30%", "20%", "HUMAN", "#39FF14"],
      ["11-25", "60%", "25%", "15%", "HUMAN", "#7BC74A"],
      ["26-39", "70%", "20%", "10%", "HUMAN", "#7BC74A"],
      ["40-59", "80%", "12%", "8%", "COMPETITIVE", "#FFD700"],
      ["60-79", "85%", "10%", "5%", "COMPETITIVE", "#D4884A"],
      ["80-89", "90%", "7%", "3%", "OPEN", "#FF6600"],
      ["90-99", "95%", "3%", "2%", "OPEN", "#FF0000"],
      ["100", "97%", "2%", "1%", "OPEN", "#FF0000"],
      ["101", "99%", "0%", "1%", "FINAL", "#FFD700"],
    ];

    y = this.table(y, {
      headers: ["STAGES", "PRIZE", "LIQUID", "RESERVE", "PHASE"],
      headerXs: [60, 170, 270, 380, 500],
      rowXs: [75, 180, 280, 390, 500],
      rows: tiers.map(([lvls, prize, liq, res, phase, color]) => [
        [lvls, "#FFFFFF"],
        [prize, "#FFD700"],
        [liq, "#00FFFF"],
        [res, "#888888"],
        [phase, color],
      ]),
      rowStep: 15,
      lineColor: 0x9945ff,
      lineAlpha: 0.2,
    });

    y += 10;
    this.line(y, 0xff00ff, 0.2);
    y += 10;
    this.t(
      x,
      y,
      "WHY: EARLY STAGES BUILD LIQUIDITY POOL",
      FONT_SIZE.CAPTION,
      "#FF00FF",
    );
    y += 16;
    this.t(
      x,
      y,
      "BY HIGH STAGES, LP IS DEEP FROM VOLUME",
      FONT_SIZE.CAPTION,
      "#FF00FF",
    );
    y += 16;
    this.t(
      x,
      y,
      "SO PRIZE % CAN SAFELY INCREASE",
      FONT_SIZE.CAPTION,
      "#FFD700",
    );
  }
}

export default DocsScene;
